import React, { useState } from 'react';
import {
  Currency as CurrencyType,
  Loadable,
  LoadableStatus,
  TaxesSummary,
  Tax,
  Regions
} from '@card/types';
import {
  Currency,
  InputLabel,
  Td,
  TableRow,
  Table as SingleHeaderTable,
  Link
} from '@increasecard/typed-components';
import {
  TableOuterWrapper,
  TableInnerWrapper,
  Table,
  Th,
  RowTh,
  TableHeaderRow,
  TableHeaderTh,
  LoadingOverlay,
  TableTotalRow,
  TotalRowTh
} from '@card/commonlib';
import { Country } from '@increasecard/increase-core';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@increasecard/icons';
import styled from 'styled-components';

const StyledArrowRightIcon = styled(ArrowRightIcon)`
  width: 32px;
  height: 24px;
  color: ${({ theme }) => theme.colorsNew.gray.black};
  :hover {
    color: ${({ theme }) => theme.colorsNew.primaryAction.regular};
  }
`;

const ArrowDown = styled(StyledArrowRightIcon)`
  transform: rotate(90deg);
`;

const ArrowUp = styled(StyledArrowRightIcon)`
  transform: rotate(-90deg);
`;

export interface TaxesSummaryTableProps {
  taxes: Loadable<TaxesSummary, Error> | undefined;
  currency: CurrencyType | undefined;
  country: Country;
}

export function TaxesSummaryTable({ taxes, currency, country }: TaxesSummaryTableProps) {
  const { t } = useTranslation('taxes');
  const [showRegion, setShowRegion] = useState<string | null>(null);

  if (!taxes || !taxes.data || !country || !currency) {
    return null;
  }

  const isLoading = taxes.status === LoadableStatus.Fetching;
  const hasData = taxes && taxes.data && !isLoading;

  const renderRegions = (regions: Regions) => {
    if (!regions) return null;
    return Object.keys(regions).map((name) => (
      <TableTotalRow key={name} variant="taxesRegion">
        <TotalRowTh variant="taxesRegion">
          <InputLabel as="div">{name}</InputLabel>
        </TotalRowTh>
        <Td rightAligned>
          <Currency currency={currency} value={regions[name]} />
        </Td>
      </TableTotalRow>
    ));
  };

  const handleRegionToggle = (category: string) => {
    setShowRegion((prevCategory) => (prevCategory === category ? null : category));
  };

  const renderCategoryRow = (category: string) => {
    if (!taxes.data) return null;
    return taxes.data[category].map((tax: Tax, index: number) => (
      <React.Fragment key={`${category}-${index}`}>
        <TableRow>
          <RowTh>
            <InputLabel as="div">
              {`${tax.categories[2]} ${tax.categories[3] || ''}`}
              {tax['regions'] !== undefined && Object.keys(tax['regions']).length !== 0 && (
                <Link
                  as="button"
                  onClick={() => handleRegionToggle(category)}
                  style={{ marginLeft: '8px' }}
                >
                  {showRegion === category ? (
                    <ArrowUp color="black" />
                  ) : (
                    <ArrowDown color="black" />
                  )}
                </Link>
              )}
            </InputLabel>
          </RowTh>
          <Td rightAligned>
            <Currency currency={currency} value={tax.amount} />
          </Td>
        </TableRow>
        {showRegion === category && renderRegions(tax['regions'])}
      </React.Fragment>
    ));
  };

  const renderCategory = (category: string) => {
    return (
      <>
        <TableHeaderRow variant="card">
          <TableHeaderTh variant="card">{category}</TableHeaderTh>
          <Th rightAligned>{t('summary.allCards')}</Th>
        </TableHeaderRow>
        {renderCategoryRow(category)}
      </>
    );
  };

  return (
    <>
      {isLoading || (!isLoading && hasData) ? (
        <LoadingOverlay active={isLoading} style={{ top: '1px', left: '-1px' }}>
          <TableOuterWrapper>
            <TableInnerWrapper>
              <Table>
                <tbody>{Object.keys(taxes.data).map((category) => renderCategory(category))}</tbody>
              </Table>
            </TableInnerWrapper>
          </TableOuterWrapper>
        </LoadingOverlay>
      ) : (
        <SingleHeaderTable hasData={false}>
          <br />
        </SingleHeaderTable>
      )}
    </>
  );
}
